import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { hideVisually } from 'polished';

import modularScale from 'styles/helpers/modular-scale';

import { ReactComponent as IconSearch } from 'icons/icon-search.svg';
import { ThemeProps } from 'types/styled';

const MainContent = styled.div`
  display: flex;
  flex: 1;
  flex-shrink: 0;
  align-items: center;

  span {
    ${hideVisually()};
  }

  svg {
    width: ${modularScale(1)};
    height: ${modularScale(1)};
  }
`;

export const SearchFieldContainer = styled.label<ThemeProps<'label'>>`
  background: ${({ theme }): string => theme.Sand};
  display: flex;
  flex-shrink: 0;
  align-items: center;
  padding: ${({ theme }): string =>
    `${theme.spacing.xxsmall} ${theme.spacing.large} ${theme.spacing.xxsmall} ${theme.spacing.medium}`};
  color: ${({ theme }): string => theme.AccentGreen};
  position: relative;
  margin-bottom: ${({ theme }): string => `${theme.spacing.medium}`};
  max-height: ${({ isSmallScreen }): string =>
    isSmallScreen ? '57px' : '46px'};

  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    background: ${({ theme }): string => theme.AccentGreen};
    width: ${({ isFocussed }): string => (isFocussed ? '100%' : '0')};
    transition: ${({ theme }): string =>
      `width ${theme.transitionDuration} ${theme.timingFunction}`};
  }
`;

const SearchFieldInput = styled.input<ThemeProps<'input'>>`
  border: 0;
  padding: ${({ theme }): string => `${theme.spacing.xsmall} 0`};
  background: none;
  color: black;
  font-family: inherit;
  font-size: inherit;
  appearance: none;
  flex: 1 1 auto;
  margin-left: ${({ theme }): string => theme.spacing.small};
  outline: none;
  min-height: ${modularScale(3)};
  box-sizing: content-box;

  &::placeholder {
    color: ${({ theme }): string => theme.CasperGrey};
    transition: ${({ theme }): string =>
      `color ${theme.transitionDuration} ${theme.timingFunction}`};
  }

  /* Change color on hover but not when focussed and hovered at the same time */
  &:hover:not(:focus)::placeholder {
    color: ${({ theme }): string => theme.DeepBlue};
  }
`;

const StyledSearchIcon = styled(IconSearch)`
  /* Prevent search icon to shrink on Safari */
  flex-shrink: 0;
`;

interface ISearchFieldProps {
  focusOnMount?: boolean;
  handleSearch: (evt: any) => void;
  placeholder?: string;
  value?: string;
  handleKeyDown?: (e: any) => void;
  isSmallScreen?: boolean;
  refObj?: any;
  children?: any;
}

const SearchField: React.FC<ISearchFieldProps> = React.forwardRef(
  (props: ISearchFieldProps, referral?: any) => {
    const {
      focusOnMount,
      handleSearch,
      placeholder = 'Search',
      value,
      handleKeyDown,
      isSmallScreen,
      refObj,
      children,
    } = props;
    const [hasFocus, setHasFocus] = useState(false);

    const refElement = refObj || referral;

    useEffect(() => {
      if (focusOnMount && refElement?.current) refElement.current.focus();
    }, [focusOnMount, refElement]);

    return (
      <SearchFieldContainer isFocussed={hasFocus} isSmallScreen={isSmallScreen}>
        <MainContent>
          <span>{placeholder}</span>
          <StyledSearchIcon />
          <SearchFieldInput
            onChange={handleSearch}
            type="search"
            value={value}
            placeholder={placeholder}
            ref={refElement}
            onFocus={(): void => setHasFocus(true)}
            onBlur={(): void => setHasFocus(false)}
            onKeyDown={handleKeyDown || undefined}
          />
        </MainContent>

        {children ? children : null}
      </SearchFieldContainer>
    );
  }
);

SearchField.displayName = 'SearchField';

export default SearchField;
